import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ExclamationCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Spin, Modal, message } from 'antd';
import Header from '~components/property-header';
import LeaveAlert from '~components/leave-alert';
import PropertyDetail from '~pages/dashboard/properties/listing-management/property-detail';
import NavigationBar, {
  showNavigationLabels,
} from '~pages/dashboard/properties/listing-management/navigation-bar';
import AddressDetail from '~pages/dashboard/properties/listing-management/address-detail';
import FacilityDetail from '~pages/dashboard/properties/listing-management/facility-detail';
import RoomDetail from '~pages/dashboard/properties/listing-management/room-detail';
import ListingDetail from '~pages/dashboard/properties/listing-management/listing-detail';
import PaymentPlan from '~pages/dashboard/properties/listing-management/payment-plan';
import GalleryManagement from '~pages/dashboard/properties/listing-management/gallery-management';
import PropertyTag from '~pages/dashboard/properties/property-tag';
import AcceptanceCriteria from '~pages/dashboard/properties/listing-management/acceptance-criteria';
import Reminder from '~pages/dashboard/properties/listing-management/pending-approval/reminder';
import SEOSetting from '~pages/dashboard/properties/listing-management/seo-setting';
import generatePath from '~settings/routing';
import { communicationStatus, platformEntity, entityAction } from '~constants';
import { bookingJourneyType } from 'common/constants/booking';
import { propertySections, propertyStatus } from '~constants/listing-management';
import { propertyDraftCategory, propertyDraftStatus } from '~constants/pending-approval';
import * as actions from '~actions/properties/listing-management';
import * as pendingApprovalActions from '~actions/pending-approval';
import { isEmptyObject } from '~helpers/property-edit';
import { fireCustomEvent } from '~helpers/custom-events';
import authControl from '~components/auth-control';
import { fetch } from '~helpers/graphql';
import * as queries from '~settings/queries';
import ReviewDraftModal from '~pages/dashboard/pending-approve/review-draft-modal';
import PendingAlertTips from '~components/pending-alert-tips';
import { PriceCalcDisplay } from './price-calc-display';
import { onlyViewPropertyRole } from '~client/helpers/auth';

const mapStateToProps = state => {
  const data = state.dashboard.listingManagement?.toJS();

  return {
    publish: data.communication.publish.status,
    communication: data.communication,
    property: data.payload,
    needConfirmRoomListings: data.needConfirmRoomListings,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyDetail: (slug, successCallback) => {
    dispatch(actions.getPropertyDetail(slug, successCallback));
  },
  getPropertyUnitTypeFacilities: successCallback => {
    dispatch(actions.getUnitTypeFacilities(successCallback));
  },
  publishProperty: (params, successCallback, validateErrCallback, inativeErrorCallback) => {
    dispatch(
      actions.publishProperty(params, successCallback, validateErrCallback, inativeErrorCallback),
    );
  },
  setProperty: property => {
    dispatch(actions.setProperty(property));
  },
  getPropertyPreview: (slug, successCallback) => {
    dispatch(actions.getPropertyPreview(slug, successCallback));
  },
  deleteRoom: (id, successCallback) => {
    dispatch(actions.deleteRoom(id, successCallback));
  },
  createRoom: (params, successCallback) => {
    dispatch(actions.createRoom(params, successCallback));
  },
  updateRoom: (params, successCallback) => {
    dispatch(actions.updateRoom(params, successCallback));
  },
  setNeedConfirmRoomListing: params => {
    dispatch(actions.setNeedConfirmRoomListing(params));
  },
  deleteListing: (id, successCallback) => {
    dispatch(actions.deleteListing(id, successCallback));
  },
  createListing: (params, successCallback) => {
    dispatch(actions.createListing(params, successCallback));
  },
  updateListing: (params, successCallback) => {
    dispatch(actions.updateListing(params, successCallback));
  },
  bulkUpdateListings: (params, successCallback) => {
    dispatch(actions.bulkUpdateListings(params, successCallback));
  },
  bulkDeleteListings: (params, successCallback, errorCallback) => {
    dispatch(actions.bulkDeleteListings(params, successCallback, errorCallback));
  },
  updateGallery: (params, successCallback) => {
    dispatch(actions.updateGallery(params, successCallback));
  },
  expirePropertyDraft: (draftIds, successCallback) => {
    dispatch(pendingApprovalActions.expirePropertyDraft(draftIds, successCallback));
  },
  processPropertyDraft: (data, success) => {
    dispatch(pendingApprovalActions.processPropertyDraft(data, success));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
@authControl(
  platformEntity.PROPERTIES_PROPERTIES,
  entityAction.READ,
  props => generatePath('property.homepage', { propertySlug: props.match.params.propertySlug }),
  onlyViewPropertyRole,
)
export default class PropertyListingManagement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isChanged: false,
      currentSection: propertySections.PROPERTY_DETAILS,
      isUpdateGallery: false,
      showReviewModal: false,
      currentSectionCategory: 'DETAIL',
      pendingDraftState: [],
      isContinueNextStep: false,
      showBookingJourneyWarning: false,
      unitTypeFacilities: null,
    };
  }

  componentDidMount() {
    this.props.getPropertyDetail(decodeURIComponent(this.props.match.params.propertySlug), () => {
      if (isEmptyObject(this.props.property)) {
        this.props.history.push(generatePath('properties', {}));
      }
      if (this.props.property && this.props.property.drafts.edges) {
        this.initDraft(this.props.property.drafts.edges, true);
      }
    });

    this.props.getPropertyUnitTypeFacilities(res => {
      if (res?.facilities) {
        this.setState({
          unitTypeFacilities: res?.facilities?.map(facility => ({
            ...facility,
            checked: false,
          })),
        });
      }
    });

    window.onbeforeunload = () => {
      if (this.state.isChanged || this.state.isUpdateGallery) {
        return true;
      }
      return null;
    };

    fireCustomEvent('triggerCloseMenu');
  }

  initDraft = (drafts, isFromComponentDidMount = false, continueNextStep = () => {}) => {
    this.setState({ showReviewModal: false });
    const draft = drafts.map(item => item.node);
    const draftArray = draft.filter(item => item.status === 'PENDING') || [];

    // according to property status show draft review modal
    let showReviewModalState = false;
    if (this.props.property.status === 'PUBLISHED') {
      showReviewModalState = isFromComponentDidMount
        ? draftArray.length > 0 && draftArray[0].category === 'DETAIL'
        : draftArray.length > 0 && draftArray[0].category === this.state.currentSectionCategory;
    } else {
      showReviewModalState = draftArray.length > 0;
    }

    this.setState(
      {
        pendingDraftState: draftArray,
        showReviewModal: showReviewModalState,
        isContinueNextStep: !isFromComponentDidMount && !showReviewModalState,
      },
      () => {
        if (this.state.isContinueNextStep) {
          continueNextStep();
        }
      },
    );
  };

  checkDraftReviewmodal = (continueNextStep = () => {}) => {
    fetch(queries.getPropertyDetail({ slug: this.props.property.slug })).then(res => {
      if (res && res.property && res.property.drafts.edges) {
        if (res.property.drafts.edges.length > 0) {
          this.props.setProperty({
            property: res.property,
          });
          this.initDraft(res.property.drafts.edges, false, continueNextStep);
        } else {
          continueNextStep();
        }
      }
    });
  };

  handleSetChanged = (isChanged = true) => {
    this.setState({
      isChanged,
    });
  };

  handleSetUpdateGallery = (isUpdateGallery = true) => {
    this.setState({
      isUpdateGallery,
    });
  };

  handleSetSection = section => {
    const { t } = this.props;
    if (this.state.isChanged) {
      Modal.confirm({
        title: t('cms.properties.edit.leave_alert.title'),
        content: t('cms.property.listing_management.leave_alert.content'),
        cancelText: t('cms.form.pop_confirm.no'),
        okText: t('cms.form.pop_confirm.yes'),
        onOk: () => {
          if (this.state.currentSection === propertySections.PROPERTY_DETAILS) {
            if (this.props.needConfirmRoomListings.some(item => item.unConfirmedFromChange)) {
              this.props.setNeedConfirmRoomListing(
                this.props.needConfirmRoomListings.filter(item => !item?.unConfirmedFromChange),
              );
            }
          }

          this.setState(
            {
              currentSection: section,
              isChanged: false,
              showBookingJourneyWarning: false,
            },
            () => {
              this.showReviewModalLogic(section);
            },
          );
        },
      });
    } else {
      this.setState(
        {
          currentSection: section,
        },
        () => {
          this.showReviewModalLogic(section);
        },
      );
    }
  };

  showReviewModalLogic = section => {
    if (section === 'PROPERTY_DETAILS') {
      this.setState({
        showReviewModal:
          this.state.pendingDraftState.filter(item => item.category === 'DETAIL').length > 0,
        currentSectionCategory: 'DETAIL',
      });
    }
    if (section === 'GALLERY_MANAGEMENT') {
      this.setState({
        showReviewModal:
          this.state.pendingDraftState.filter(item => item.category === 'GALLERY').length > 0,
        currentSectionCategory: 'GALLERY',
      });
    }
  };

  handleSetNextSection = updateParams => {
    const currBookingJourney = updateParams.bookingJourney;
    const prevBookingJourney = this.props.property.bookingJourney;

    const isBookingJourneyChanged =
      prevBookingJourney !== currBookingJourney &&
      [prevBookingJourney, currBookingJourney].includes(bookingJourneyType.INSTANT_BOOK);

    const sections = showNavigationLabels({
      ...this.props.property,
      bookingJourney: isBookingJourneyChanged ? currBookingJourney : prevBookingJourney,
    });

    const currentSectionIndex = sections.indexOf(this.state.currentSection);

    this.setState(
      {
        isChanged: false,
        currentSection:
          currentSectionIndex === sections.length - 1
            ? this.state.currentSection
            : sections[currentSectionIndex + 1],
      },
      () => {
        this.props.setProperty({
          property: updateParams,
        });

        if (currentSectionIndex === 0 && isBookingJourneyChanged) {
          this.props.getPropertyDetail(
            decodeURIComponent(this.props.match.params.propertySlug),
            () => {
              const currentListings = this.props.property.unitTypes.edges
                ?.map(item => ({
                  unitId: item.node.id,
                  unConfirmedFromChange: true,
                  listings: item.node.listings,
                }))
                .filter(item => item.listings.length > 0);

              const currentCopiedListings = this.props.needConfirmRoomListings.filter(
                item => item.unConfirmedFromCopy,
              );

              this.props.setNeedConfirmRoomListing(currentListings.concat(currentCopiedListings));
            },
          );
        } else {
          if (updateParams.slug) {
            this.props.history.push(
              generatePath('property.edit', { propertySlug: updateParams.slug }),
            );
          }
        }
      },
    );
  };

  handlePreview = callback => {
    this.props.getPropertyPreview(decodeURIComponent(this.props.match.params.propertySlug), res => {
      callback(res.property);
    });
  };

  handlePublish = (
    params = { isPublish: false, unpublishReason: null },
    successCallback = () => {},
  ) => {
    if (this.state.isChanged) {
      if (this.propertyDetail) {
        const promise = this.propertyDetail.handleSubmit();
        if (promise) {
          promise.then(res => {
            if (res.isUpdated) {
              this.executePublishProperty(params, successCallback);
            }
          });
        }
      }
      if (this.addressDetail) {
        const promise = this.addressDetail.handleSave();
        if (promise) {
          promise.then(res => {
            if (res.isUpdated) {
              this.executePublishProperty(params, successCallback);
            }
          });
        }
      }
      if (this.facilityDetail) {
        const promise = this.facilityDetail.handleSave();
        if (promise) {
          promise.then(res => {
            if (res.isUpdated) {
              this.executePublishProperty(params, successCallback);
            }
          });
        }
      }
      return;
    }

    if (this.state.isUpdateGallery) {
      if (this.galleryManagement) {
        this.galleryManagement.handleSubmit()?.then(res => {
          if (res.isUpdated) {
            this.executePublishProperty(params, successCallback);
          }
        });
      }
      return;
    }

    this.executePublishProperty(params, successCallback);
  };

  landlordTipsModal = (isPublish, code = '') => {
    const { property, t } = this.props;
    if (
      property &&
      (property.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC ||
        property.bookingJourney === bookingJourneyType.INSTANT_BOOK) &&
      isPublish
    ) {
      const isNotBoundPayout = code === 'INVALID_PROPERTY_PAYOUT_METHOD';
      const modal = Modal.confirm({
        className: 'property-listing-management__landlord-tips-modal',
        title: (
          <div>
            <CloseOutlined
              onClick={() => modal.destroy()}
              className="property-listing-management__landlord-tips-modal__close"
            />
            {isNotBoundPayout
              ? t('cms.property.listing_management.no_payouts_method.tips')
              : t('cms:property_listing_management_nopayment_tip')}
          </div>
        ),
        icon: <ExclamationCircleFilled />,
        okText: t('cms.property.listing_management.btn.got_it'),
        width: 380,
      });
    }
  };
  noRentPaymentWarningModal = () => {
    const { t } = this.props;
    Modal.confirm({
      className: 'property-listing-management__landlord-tips-modal',
      title: t('cms:property_listing_management_inactive_payment'),
      icon: <ExclamationCircleFilled />,
      okText: t('cms.property.listing_management.btn.got_it'),
      width: 380,
    });
  };
  executePublishProperty = (params, successCallback) => {
    const { property, t } = this.props;

    this.props.publishProperty(
      {
        publish: params.isPublish,
        id: property.id,
        ...(params.isPublish ? {} : { unpublishReason: params.unpublishReason }),
      },
      () => {
        successCallback();
        message.success(
          t(`cms.properties.edit.toast.${params.isPublish ? 'publish' : 'unpublish'}_success`),
        );
        this.props.setProperty({
          property: {
            status: params.isPublish ? propertyStatus.PUBLISHED : propertyStatus.UNPUBLISHED,
          },
        });
      },
      code => {
        this.landlordTipsModal(params.isPublish, code);
      },
      () => {
        this.noRentPaymentWarningModal();
      },
    );
  };

  onGotoRoomConfig = () => {
    this.handleSetSection(propertySections.ROOM_CONFIG);
  };

  handleClearUnconfirmedListings = () => {
    this.props.setNeedConfirmRoomListing([]);
  };

  getCategoryRejectedDrafts = currentSection => {
    const { property } = this.props;

    if (!property || !property.drafts || property.drafts.edges.length === 0) {
      return [];
    }

    const propertyDrafts = property.drafts.edges.map(draft => draft.node);

    if (currentSection === propertySections.PROPERTY_DETAILS) {
      const detailRejectDraft = propertyDrafts.filter(
        draft =>
          draft.category === propertyDraftCategory.DETAIL &&
          draft.status === propertyDraftStatus.REJECTED,
      );

      return detailRejectDraft;
    }

    if (currentSection === propertySections.GALLERY_MANAGEMENT) {
      const galleryRejectDrafts = propertyDrafts.filter(
        draft =>
          draft.category === propertyDraftCategory.GALLERY &&
          draft.status === propertyDraftStatus.REJECTED,
      );

      return galleryRejectDrafts;
    }

    return [];
  };

  handleClickReviewDraftModal = () => {
    this.setState({ showReviewModal: false });
  };

  handleBookingTypeChange = val => {
    const isChanged = val !== this.props.property?.bookingJourney;

    if (!isChanged) return;

    this.setState({
      showBookingJourneyWarning:
        val === bookingJourneyType.INSTANT_BOOK && this.props.property?.bookingJourney !== val,
    });
  };

  render() {
    const {
      t,
      property,
      history,
      communication,
      processPropertyDraft,
      expirePropertyDraft,
      deleteRoom,
      createRoom,
      updateRoom,
      setProperty,
      deleteListing,
      createListing,
      updateListing,
      bulkUpdateListings,
      bulkDeleteListings,
      updateGallery,
      needConfirmRoomListings,
      setNeedConfirmRoomListing,
    } = this.props;
    const {
      isChanged,
      currentSection,
      showReviewModal,
      currentSectionCategory,
      showBookingJourneyWarning,
    } = this.state;
    const categoryRejectedDrafts = this.getCategoryRejectedDrafts(currentSection);

    const hasWarningListingRooms = property?.unitTypes?.edges
      ?.map(room => {
        const warningListings = room.node.listings.filter(listing => {
          if (property.bookingJourney === bookingJourneyType.INSTANT_BOOK) {
            return !listing.inventoryCount && listing.inventoryCount !== 0;
          } else {
            return !listing.availability;
          }
        });

        return {
          id: room.node.id,
          warningListings,
        };
      })
      .filter(item => item.warningListings?.length);

    return (
      <div className="property-listing-management">
        <LeaveAlert
          history={history}
          t={t}
          when={isChanged || needConfirmRoomListings.length > 0}
          onConfirmLeavePage={this.handleClearUnconfirmedListings}
        />
        <Choose>
          <When
            condition={communication.getDetail.status !== communicationStatus.IDLE || !property}
          >
            <div className="property-edit property-listing-management__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <Header t={t} property={property} type="listing-management" />
            <If condition={property && !onlyViewPropertyRole()}>
              <div className="property-listing-management__pending-tip">
                <PendingAlertTips property={property} history={history} />
              </div>
            </If>
            <div className="property-listing-management__main-wrap">
              <NavigationBar
                t={t}
                property={property}
                currentSection={currentSection}
                handleSetSection={this.handleSetSection}
                publishStatus={communication.publish.status}
                handlePreview={this.handlePreview}
                handlePublish={this.handlePublish}
                isHaveUnconfirmedListing={
                  needConfirmRoomListings.length > 0 || hasWarningListingRooms.length > 0
                }
                history={history}
                onClearUnconfirmedListings={this.handleClearUnconfirmedListings}
              />
              <div className="property-listing-management__right">
                <If condition={categoryRejectedDrafts.length > 0}>
                  <div className="property-listing-management__part-reminder">
                    <Reminder t={t} draft={categoryRejectedDrafts[0]} />
                  </div>
                </If>
                <Choose>
                  <When condition={currentSection === propertySections.PROPERTY_DETAILS}>
                    <PropertyDetail
                      t={t}
                      property={property}
                      showBookingJourneyWarning={showBookingJourneyWarning}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      onRef={node => {
                        this.propertyDetail = node;
                      }}
                      rejectedDraftIds={categoryRejectedDrafts.map(draft => draft.id)}
                      expirePropertyDraft={expirePropertyDraft}
                      checkDraftReviewmodal={this.checkDraftReviewmodal}
                      setNeedConfirmRoomListing={setNeedConfirmRoomListing}
                      handleBookingTypeChange={this.handleBookingTypeChange}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PROPERTY_ADDRESS}>
                    <AddressDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      onRef={node => {
                        this.addressDetail = node;
                      }}
                    />
                  </When>
                  <When condition={currentSection === propertySections.ACCEPTANCE_CRITERIA}>
                    <AcceptanceCriteria
                      t={t}
                      property={property}
                      afterSave={this.handleSetNextSection}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PROPERTY_FACILITY}>
                    <FacilityDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      onRef={node => {
                        this.facilityDetail = node;
                      }}
                    />
                  </When>
                  <When condition={currentSection === propertySections.ROOM_CONFIG}>
                    <RoomDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      afterSave={this.handleSetNextSection}
                      deleteRoom={deleteRoom}
                      createRoom={createRoom}
                      updateRoom={updateRoom}
                      setProperty={setProperty}
                      setNeedConfirmRoomListing={setNeedConfirmRoomListing}
                      needConfirmRoomListings={needConfirmRoomListings}
                      communication={communication}
                      unitTypeFacilities={this.state.unitTypeFacilities}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PRICE_AND_AVAILABILITY}>
                    <ListingDetail
                      t={t}
                      property={property}
                      onChange={this.handleSetChanged}
                      hasWarningListingRooms={hasWarningListingRooms}
                      needConfirmRoomListings={needConfirmRoomListings}
                      deleteListing={deleteListing}
                      createListing={createListing}
                      updateListing={updateListing}
                      refetch={this.checkDraftReviewmodal}
                      bulkUpdateListings={bulkUpdateListings}
                      bulkDeleteListings={bulkDeleteListings}
                      setNeedConfirmRoomListing={setNeedConfirmRoomListing}
                      setProperty={setProperty}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PRICE_CALC_DISPLAY}>
                    <PriceCalcDisplay
                      t={t}
                      property={property}
                      setProperty={this.props.setProperty}
                      afterSave={this.handleSetNextSection}
                      updateChangedState={this.handleSetChanged}
                    />
                  </When>
                  <When condition={currentSection === propertySections.PAYMENT_PLAN}>
                    <PaymentPlan
                      t={t}
                      property={property}
                      setProperty={this.props.setProperty}
                      afterSave={this.handleSetNextSection}
                    />
                  </When>
                  <When condition={currentSection === propertySections.TAG}>
                    <PropertyTag property={property} afterSave={this.handleSetNextSection} />
                  </When>
                  <When condition={currentSection === propertySections.SEO_SETTING}>
                    <SEOSetting property={property} afterSave={this.handleSetNextSection} />
                  </When>
                </Choose>

                <GalleryManagement
                  t={t}
                  property={property}
                  onRef={node => {
                    this.galleryManagement = node;
                  }}
                  handleGotoRoomConfig={this.onGotoRoomConfig}
                  updateGallery={updateGallery}
                  setProperty={setProperty}
                  onSetUpdateGallery={this.handleSetUpdateGallery}
                  isHidden={currentSection !== propertySections.GALLERY_MANAGEMENT}
                  checkDraftReviewmodal={this.checkDraftReviewmodal}
                />
              </div>
            </div>
          </Otherwise>
        </Choose>

        <If condition={showReviewModal}>
          <ReviewDraftModal
            activeModal
            t={t}
            onClose={this.handleClickReviewDraftModal}
            originalData={property}
            processPropertyDraft={processPropertyDraft}
            draftsCategory={[currentSectionCategory]}
            isShowCloseBtn={false}
          />
        </If>
      </div>
    );
  }
}

PropertyListingManagement.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.object,
  getPropertyDetail: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      propertySlug: PropTypes.string,
    }).isRequired,
  }).isRequired,
  property: PropTypes.object,
  publishProperty: PropTypes.func.isRequired,
  setProperty: PropTypes.func.isRequired,
  communication: PropTypes.object,
  getPropertyPreview: PropTypes.func.isRequired,
  deleteRoom: PropTypes.func.isRequired,
  createRoom: PropTypes.func,
  updateRoom: PropTypes.func,
  setNeedConfirmRoomListing: PropTypes.func,
  needConfirmRoomListings: PropTypes.array,
  deleteListing: PropTypes.func,
  createListing: PropTypes.func,
  updateListing: PropTypes.func,
  bulkUpdateListings: PropTypes.func,
  bulkDeleteListings: PropTypes.func,
  updateGallery: PropTypes.func,
  expirePropertyDraft: PropTypes.func,
  processPropertyDraft: PropTypes.func,
};

PropertyListingManagement.defaultProps = {
  t: () => {},
  history: {},
  getPropertyDetail: () => {},
  match: {
    params: {
      propertySug: '',
    },
  },
  property: {},
  publishProperty: () => {},
  setProperty: () => {},
  communication: {},
  getPropertyPreview: () => {},
  deleteRoom: () => {},
  needConfirmRoomListings: [],
  setNeedConfirmRoomListing: () => {},
  createRoom: () => {},
  updateRoom: () => {},
  deleteListing: () => {},
  createListing: () => {},
  updateListing: () => {},
  bulkUpdateListings: () => {},
  bulkDeleteListings: () => {},
  updateGallery: () => {},
  expirePropertyDraft: () => {},
  processPropertyDraft: () => {},
};
